import { SCHEMA_ORG, TYPES, DATA_POINTS } from './constants.js';

// Shared MonetaryAmount builder — used for return shipping fees and restocking fees
function buildMonetaryAmount(amount, currency = DATA_POINTS.locale.currency) {
  if (amount === undefined || amount === null) return undefined;

  // If amount is 0, there is no real fee — omit the node entirely
  if (amount === 0) return undefined;

  if (typeof amount === 'object') {
    const value = amount.value ?? amount.amount;
    if (!value) return undefined;
    return {
      '@type': TYPES.monetary_amount,
      value,
      currency: amount.currency ?? currency,
    };
  }

  return {
    '@type': TYPES.monetary_amount,
    value: amount,
    currency,
  };
}

// Kept as a named export for external callers (e.g. restocking fee use cases)
export function buildRestockingFee(amount, currency = DATA_POINTS.locale.currency) {
  return buildMonetaryAmount(amount, currency);
}

export function buildSeasonalOverride(override = {}) {
  if (!override || !override.startDate || !override.endDate) return undefined;

  return {
    "@type": TYPES.merchant_return_policy_seasonal_override,
    "startDate": override.startDate,
    "endDate": override.endDate,
    "returnPolicyCategory": override.returnPolicyCategory || SCHEMA_ORG.finite,
    "merchantReturnDays": override.days || DATA_POINTS.returns.seasonal.defaultDays,
  };
}

export function buildMerchantReturnPolicy(policy = {}) {
  if (!policy) return undefined;

  const category = policy.returnPolicyCategory ?? SCHEMA_ORG.finite;
  const isNotPermitted = category === SCHEMA_ORG.notPermitted;
  const currency = policy.currency ?? DATA_POINTS.locale.currency;

  const returnShippingFeesAmount = buildMonetaryAmount(
    policy.returnShippingFeesAmount,
    currency
  );

  const feeAppliesTo = [SCHEMA_ORG.returnFeesCustomerResponsibility, SCHEMA_ORG.shippingFees];

  return {
    '@type': TYPES.merchant_return_policy,

    applicableCountry: policy.applicableCountry ?? [DATA_POINTS.shipping.country.US, DATA_POINTS.shipping.country.CA],
    returnPolicyCategory: category,

    ...(!isNotPermitted && {
      merchantReturnDays: policy.returnDays ?? DATA_POINTS.returns.standard.returnDays,
      ...(policy.itemCondition && {
        itemCondition: policy.itemCondition,
      }),
      returnMethod: policy.returnMethod ?? SCHEMA_ORG.byMail,
      returnFees: policy.returnFees ?? SCHEMA_ORG.free,
      refundType: policy.refundType ?? SCHEMA_ORG.full,

      ...(policy.returnLabelSource && {
        returnLabelSource: policy.returnLabelSource,
      }),

      ...(feeAppliesTo.includes(policy.returnFees) && returnShippingFeesAmount && {
        returnShippingFeesAmount,
      }),
      
      ...(policy.customerRemorseReturnFees && {
        customerRemorseReturnFees: policy.customerRemorseReturnFees,
      }),

      ...(policy.customerRemorseReturnLabelSource && {
        customerRemorseReturnLabelSource: policy.customerRemorseReturnLabelSource,
      }),

      ...(policy.itemDefectReturnLabelSource && {
        itemDefectReturnLabelSource: policy.itemDefectReturnLabelSource,
      }),

      ...(policy.restockingFee !== undefined && {
        restockingFee: buildMonetaryAmount(policy.restockingFee.amount ?? policy.restockingFee, currency),
      }),
    }),
    
    ...(policy.merchantReturnLink && {
      merchantReturnLink: policy.merchantReturnLink,
    }),

    ...(policy.inStoreReturnsOffered && {
      inStoreReturnsOffered: policy.inStoreReturnsOffered,
    }),

    ...(policy.seasonalOverride && {
      returnPolicySeasonalOverride: buildSeasonalOverride(policy.seasonalOverride),
    }),

    // Always free return for defects as per statutory rights
    itemDefectReturnFees: SCHEMA_ORG.free,
  };
}

export function buildMattressReturnPolicy(options = {}) {
  return buildMerchantReturnPolicy({
    applicableCountry: [DATA_POINTS.shipping.country.US, DATA_POINTS.shipping.country.CA],
    returnPolicyCategory: SCHEMA_ORG.finite,
    returnDays: DATA_POINTS.returns.mattress.returnDays,
    returnMethod: SCHEMA_ORG.byMail,
    returnFees: SCHEMA_ORG.free,
    refundType: [SCHEMA_ORG.exchange, SCHEMA_ORG.full],
    merchantReturnLink: DATA_POINTS.url.return_link,
    itemCondition: SCHEMA_ORG.newCondition,
    ...options,
  });
}

export function buildStandardReturnPolicy(options = {}) {
  return buildMerchantReturnPolicy({
    applicableCountry: [DATA_POINTS.shipping.country.US, DATA_POINTS.shipping.country.CA],
    returnPolicyCategory: SCHEMA_ORG.finite,
    returnDays: DATA_POINTS.returns.standard.returnDays,
    returnMethod: SCHEMA_ORG.byMail,
    returnFees: SCHEMA_ORG.free,
    refundType: [SCHEMA_ORG.exchange, SCHEMA_ORG.full],
    merchantReturnLink: DATA_POINTS.url.return_link,
    itemCondition: SCHEMA_ORG.newCondition,
    ...options,
  });
}

export function buildReturnPolicyWithSeasonalOverride(policy = {}, override = {}) {
  return buildMerchantReturnPolicy({
    ...policy,
    seasonalOverride: override
  });
}

export function buildChristmasReturnPolicy(options = {}) {
  return buildReturnPolicyWithSeasonalOverride(
    {
      // Explicitly inherits all fields from buildStandardReturnPolicy for consistency
      applicableCountry: [DATA_POINTS.shipping.country.US, DATA_POINTS.shipping.country.CA],
      returnPolicyCategory: SCHEMA_ORG.finite,
      returnDays: DATA_POINTS.returns.standard.returnDays,
      returnMethod: SCHEMA_ORG.byMail,
      returnFees: SCHEMA_ORG.free,
      refundType: [SCHEMA_ORG.exchange, SCHEMA_ORG.full],
      merchantReturnLink: DATA_POINTS.url.return_link,
      itemCondition: SCHEMA_ORG.newCondition,
      ...options,
    },
    {
      startDate: options.seasonalStart || DATA_POINTS.returns.seasonal.startDate,
      endDate: options.seasonalEnd || DATA_POINTS.returns.seasonal.endDate,
      days: options.seasonalDays || DATA_POINTS.returns.seasonal.defaultDays,
    }
  );
}

export function buildExtendedReturnPolicy(options = {}) {
  return buildMerchantReturnPolicy({
    applicableCountry: [DATA_POINTS.shipping.country.US, DATA_POINTS.shipping.country.CA],
    returnPolicyCategory: SCHEMA_ORG.finite,
    // Extended policy defaults to the seasonal window (60 days), not the standard 30
    returnDays: DATA_POINTS.returns.seasonal.defaultDays,
    returnMethod: SCHEMA_ORG.byMail,
    returnFees: SCHEMA_ORG.free,
    refundType: [SCHEMA_ORG.exchange, SCHEMA_ORG.full],
    merchantReturnLink: DATA_POINTS.url.return_link,
    itemCondition: SCHEMA_ORG.newCondition,
    ...options,
  });
}

export function buildNoReturnPolicy(options = {}) {
  return buildMerchantReturnPolicy({
    applicableCountry: [DATA_POINTS.shipping.country.US, DATA_POINTS.shipping.country.CA],
    returnPolicyCategory: SCHEMA_ORG.notPermitted,
    merchantReturnLink: DATA_POINTS.url.return_link,
    ...options,
  });
}

export function buildFinalSalePolicy(options = {}) {
  return buildNoReturnPolicy(options);
}

export function buildInStoreReturnPolicy(options = {}) {
  return buildMerchantReturnPolicy({
    applicableCountry: [DATA_POINTS.shipping.country.US, DATA_POINTS.shipping.country.CA],
    returnPolicyCategory: SCHEMA_ORG.finite,
    returnDays: DATA_POINTS.returns.standard.returnDays,
    returnMethod: SCHEMA_ORG.inStore,
    returnFees: SCHEMA_ORG.free,
    refundType: [SCHEMA_ORG.exchange, SCHEMA_ORG.full],
    merchantReturnLink: DATA_POINTS.url.return_link,
    itemCondition: SCHEMA_ORG.newCondition,
    inStoreReturnsOffered: true,
    ...options,
  });
}

export function buildMultiCountryReturnPolicy(policies = []) {
  return policies.map(policy => buildMerchantReturnPolicy(policy)).filter(Boolean);
}

// Internal map: policyKey (from constants) → builder function.
// When a new policy type is needed, add the builder here and map it in constants.js.
const POLICY_BUILDERS = {
  mattress: buildMattressReturnPolicy,
  standard: buildStandardReturnPolicy,
};

/**
 * Centralized product-type detection helper.
 * Use this in OfferSchema and AggregateOfferSchema to avoid duplicating
 * the productType check in multiple files.
 * To add a new product type: add it to DATA_POINTS.returns.productTypePolicies in constants.js.
 * @param {string} productType - The Shopify product type string.
 * @returns {Object} The correct MerchantReturnPolicy object for this product.
 */
export function birchReturnPolicy(productType) {
  const type = (productType || '').toLowerCase();
  const policyKey = DATA_POINTS.returns.productTypePolicies[type];
  const builder = POLICY_BUILDERS[policyKey] ?? buildStandardReturnPolicy;
  return builder();
}